(() => {
  const LANGUAGE_KEY = 'amazear-video-language';
  const normalize = value => String(value || 'und').trim().toLowerCase().replace(/_/g, '-');
  const sameLanguage = (a, b) => a === b || a.split('-')[0] === b.split('-')[0];
  const safeSource = source => source && /^(https:)?\/\//i.test(source.src) && typeof source.type === 'string';
  const isHls = source => /mpegurl/i.test(source.type);
  let hlsLibrary;
  function loadHls(url) {
    if (window.Hls) return Promise.resolve(window.Hls);
    if (!url) return Promise.resolve(null);
    if (!hlsLibrary) hlsLibrary = new Promise(resolve => {
      const script = document.createElement('script');
      const timer = setTimeout(() => finish(null), 8000);
      const finish = value => { clearTimeout(timer); script.onload = script.onerror = null; resolve(value); };
      script.src = url;
      script.async = true;
      script.onload = () => finish(window.Hls || null);
      script.onerror = () => finish(null);
      document.head.append(script);
    });
    return hlsLibrary;
  }
  const declaredVersions = [
    { label: 'Deutsch', language: 'de', pattern: /\[(?:German Version|Deutsche Version)[;\]]/i },
    { label: '日本語', language: 'ja', pattern: /\[Japanese Version[;\]]|［日本語版[・；］]/i },
    { label: 'Français', language: 'fr', pattern: /\[(?:French Version|Version française)[;\]]/i },
    { label: 'English', language: 'en', pattern: /\[English Version[;\]]/i },
    { label: 'Italiano', language: 'it', pattern: /\[(?:Italian Version|Versione italiana)[;\]]/i },
    { label: 'Español', language: 'es', pattern: /\[(?:Spanish Version|Versión en español)[;\]]/i },
    { label: '中文', language: 'zh-CN', pattern: /\[Chinese Version[;\]]|［中文版[；］]/i }
  ];
  class AmazearVideoGuide extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      const dataNode = this.querySelector('[data-video-clips]');
      this.player = this.querySelector('video');
      if (!dataNode || !this.player) return;
      let data;
      try { data = JSON.parse(dataNode.textContent); } catch { return; }
      this.clips = (data.clips || []).filter(clip => Array.isArray(clip.sources) && clip.sources.some(safeSource)).map(clip => ({ ...clip, id: String(clip.id) }));
      if (!this.clips.length) return;
      this.initialized = true;
      this.originalLabel = data.originalLabel || 'Original video';
      this.buttons = [...this.querySelectorAll('[data-video-item]')];
      this.select = this.querySelector('[data-video-language]');
      this.languageControl = this.querySelector('[data-video-language-control]');
      this.titleNode = this.querySelector('[data-video-title]');
      this.descriptionNode = this.querySelector('[data-video-description]');
      this.errorNode = this.querySelector('[data-video-error]');
      this.chooser = this.querySelector('[data-video-chooser]');
      this.clipSelect = this.querySelector('[data-video-choice]');
      this.versionGroup = this.querySelector('[data-video-versions]');
      this.versionButtons = [];
      this.setupPlayback(data);
      this.setupChooser();
      this.languages = [...new Set(this.clips.map(clip => normalize(clip.language)))];
      this.select.replaceChildren();
      this.languages.forEach(language => {
        const clip = this.clips.find(item => normalize(item.language) === language);
        const option = document.createElement('option');
        option.value = language;
        option.textContent = this.languageName(language, clip.languageName);
        this.select.append(option);
      });
      this.languageControl.hidden = this.languages.length < 2;
      let saved;
      try { saved = localStorage.getItem(LANGUAGE_KEY); } catch { /* Private browsing can block storage. */ }
      const desired = [saved, ...(navigator.languages || [navigator.language])].filter(Boolean).map(normalize);
      const chosen = desired.map(language => this.languages.includes(language) ? language : this.languages.find(item => sameLanguage(item, language))).find(Boolean) || (this.languages.includes('und') ? 'und' : this.languages[0]);
      this.setLanguage(chosen, false);
      this.onLanguageChange = () => {
        this.setLanguage(this.select.value, false);
        try { localStorage.setItem(LANGUAGE_KEY, this.select.value); } catch { /* Playback works without storage. */ }
      };
      this.onClipClick = event => {
        const versionButton = event.target.closest('[data-video-version]');
        if (versionButton && this.versionGroup?.contains(versionButton)) {
          const clip = this.clips.find(item => item.id === versionButton.dataset.clipId);
          if (clip) this.setLanguage(normalize(clip.language), false, clip.id);
          return;
        }
        const button = event.target.closest('[data-video-item]');
        if (!button || !this.contains(button)) return;
        const clip = this.clips.find(item => item.id === button.dataset.clipId);
        if (clip && normalize(clip.language) === this.select.value) this.showClip(clip, true);
      };
      this.onError = () => this.recoverPlayback('media_error');
      this.onClipChange = () => {
        const clip = this.clips.find(item => item.id === this.clipSelect.value);
        if (clip) this.setLanguage(normalize(clip.language), false, clip.id);
      };
      this.select.addEventListener('change', this.onLanguageChange);
      this.clipSelect?.addEventListener('change', this.onClipChange);
      this.addEventListener('click', this.onClipClick);
      this.player.addEventListener('error', this.onError);
    }
    disconnectedCallback() {
      if (!this.initialized) return;
      this.player.pause();
      this.select.removeEventListener('change', this.onLanguageChange);
      this.clipSelect?.removeEventListener('change', this.onClipChange);
      this.removeEventListener('click', this.onClipClick);
      this.player.removeEventListener('error', this.onError);
      this.resetPlayback();
      this.observer?.disconnect();
      this.playbackEvents.forEach(([name, listener]) => this.player.removeEventListener(name, listener));
      this.qualitySelect?.removeEventListener('change', this.onQuality);
      this.playButton?.removeEventListener('click', this.onPlayButton);
      this.retryButton?.removeEventListener('click', this.onRetry);
      this.smoothButton?.removeEventListener('click', this.onSmooth);
      this.initialized = false;
    }
    setupPlayback(data) {
      this.hlsUrl = this.dataset.hlsUrl || data.hlsUrl;
      this.strings = this.querySelector('[data-video-playback]')?.dataset || {};
      this.qualitySelect = this.querySelector('[data-video-quality]');
      this.playButton = this.querySelector('[data-video-play]');
      this.retryButton = this.querySelector('[data-video-retry]');
      this.smoothButton = this.querySelector('[data-video-smooth]');
      this.statusNode = this.querySelector('[data-video-status]');
      this.querySelector('[data-video-playback]')?.removeAttribute('hidden');
      this.generation = 0;
      this.mode = 'auto';
      this.mobile = window.matchMedia?.('(max-width: 749px)').matches || window.innerWidth <= 749;
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      this.weakConnection = Boolean(connection?.saveData || /(^|slow-)2g/.test(connection?.effectiveType || '') || (connection?.downlink > 0 && connection.downlink < 1.5));
      this.onQuality = () => this.changeQuality(this.qualitySelect.value);
      this.qualitySelect?.addEventListener('change', this.onQuality);
      this.onPlayButton = async () => {
        if (this.playButton?.disabled) return;
        const generation = this.generation;
        this.startRequested = this.startRequested || performance.now();
        if (this.playButton) this.playButton.disabled = true;
        this.status('loading');
        await this.prepareAdaptive();
        if (!this.isConnected || generation !== this.generation) return;
        if (this.playButton) this.playButton.disabled = false;
        this.requestPlay();
      };
      this.onRetry = () => {
        this.metric('retry');
        const time = this.player.currentTime || 0;
        this.resetPlayback();
        this.fallbackTried = true;
        this.installSource(this.mp4Source(), time, true);
      };
      this.onSmooth = () => this.changeQuality('480');
      this.playButton?.addEventListener('click', this.onPlayButton);
      this.retryButton?.addEventListener('click', this.onRetry);
      this.smoothButton?.addEventListener('click', this.onSmooth);
      this.playbackEvents = [
        ['play', () => {
          this.wanted = true;
          if (!this.startRequested) this.startRequested = performance.now();
          if (this.playButton) this.playButton.hidden = true;
          this.hls?.startLoad(this.player.currentTime || this.resumeAt || -1);
          this.status('loading');
          this.watchStall();
        }],
        ['playing', () => {
          clearTimeout(this.stallTimer); this.stallTimer = null;
          this.status('');
          if (!this.started) {
            this.started = true;
            this.metric('start', { startup_ms: Math.round(performance.now() - (this.startRequested || performance.now())) });
          }
          if (this.waitStarted) this.metric('buffer_end', { buffer_ms: Math.round(performance.now() - this.waitStarted) });
          this.waitStarted = 0;
        }],
        ['waiting', () => {
          if (!this.wanted || this.switching || this.player.seeking) return;
          if (!this.waitStarted) { this.waitStarted = performance.now(); this.metric('buffer'); }
          this.status('loading');
          this.watchStall();
        }],
        ['pause', () => {
          if (this.switching) return;
          this.wanted = false;
          clearTimeout(this.stallTimer); this.stallTimer = null;
          this.status('');
        }],
        ['ended', () => { this.wanted = false; clearTimeout(this.stallTimer); this.stallTimer = null; this.metric('complete'); }]
      ];
      this.playbackEvents.forEach(([name, listener]) => this.player.addEventListener(name, listener));
      if ('IntersectionObserver' in window && !this.weakConnection) {
        this.observer = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          this.player.preload = 'metadata';
          this.prepareAdaptive();
        }, { rootMargin: '200px' });
        this.observer.observe(this);
      }
    }
    status(key) {
      if (this.statusNode) { this.statusNode.textContent = this.strings[key] || ''; this.statusNode.hidden = !key; }
    }
    metric(action, parameters = {}) {
      const detail = { action, video_id: this.currentClip?.id, quality: this.mode, delivery: this.sourceKind || 'mp4', ...parameters };
      this.dispatchEvent(new CustomEvent('amazear:video-metric', { bubbles: true, detail }));
      window.AmazearAnalytics?.track('video_' + action, detail);
    }
    playbackSources() {
      const saved = this.currentClip?.playback;
      const playback = saved?.originalId && String(saved.originalId).split('/').pop() !== String(this.currentClip?.videoId).split('/').pop() ? null : saved;
      const original = (Array.isArray(playback?.original) ? playback.original : this.currentClip?.sources || []).filter(safeSource);
      const mobile = (Array.isArray(playback?.mobile) ? playback.mobile : []).filter(safeSource);
      return { original, mobile };
    }
    autoSources() {
      const { original, mobile } = this.playbackSources();
      return (this.mobile || this.weakConnection) && mobile.length ? mobile : original;
    }
    mp4Source(height) {
      const { original, mobile } = this.playbackSources();
      const target = height || (this.mode === 'auto' ? (this.weakConnection ? 480 : this.mobile ? 720 : 1080) : Number(this.mode));
      if (!height && this.mode === 'auto' && (this.mobile || this.weakConnection)) {
        const delivery = mobile.filter(item => item.type === 'video/mp4' && Number(item.height) <= target).sort((a, b) => b.height - a.height)[0];
        if (delivery) return delivery;
      }
      const sources = [...(target <= 720 ? mobile : []), ...original].filter(item => item.type === 'video/mp4');
      return sources.find(item => Number(item.height) === target) || sources.filter(item => Number(item.height) <= target).sort((a, b) => b.height - a.height)[0] || sources.sort((a, b) => a.height - b.height)[0];
    }
    updateQualities() {
      if (!this.qualitySelect) return;
      this.qualitySelect.replaceChildren();
      const { original, mobile } = this.playbackSources();
      const heights = [...new Set([...mobile, ...original].filter(item => item.type === 'video/mp4').map(item => Number(item.height)).filter(Boolean))].sort((a, b) => a - b);
      [['auto', this.strings.auto || 'Auto'], ...heights.map(height => [String(height), height + 'p' + (height <= 480 ? ' · ' + (this.strings.smooth || 'Smooth') : height >= 1080 ? ' · HD' : '')])].forEach(([value, label]) => {
        const option = document.createElement('option'); option.value = value; option.textContent = label; this.qualitySelect.append(option);
      });
      if (![...this.qualitySelect.options].some(option => option.value === this.mode)) this.mode = 'auto';
      this.qualitySelect.value = this.mode;
      if (this.smoothButton) this.smoothButton.hidden = !heights.includes(480);
    }
    resetPlayback() {
      this.generation += 1;
      clearTimeout(this.stallTimer);
      this.stallTimer = null;
      this.hls?.destroy();
      this.hls = null;
      this.preparing = null;
      if (this.restorePosition) this.player.removeEventListener('loadedmetadata', this.restorePosition);
      this.restorePosition = null;
    }
    installSource(source, time = 0, resume = false) {
      if (!source) { this.failPlayback(); return; }
      this.switching = true;
      this.player.pause();
      this.player.removeAttribute('src');
      this.player.removeAttribute('autoplay');
      this.player.replaceChildren();
      const node = document.createElement('source');
      node.src = source.src.startsWith('//') ? 'https:' + source.src : source.src;
      node.type = source.type;
      this.player.append(node);
      this.sourceKind = isHls(source) ? 'native_hls' : 'mp4';
      this.restorePosition = () => {
        if (time > 0) { try { this.player.currentTime = Math.min(time, Number.isFinite(this.player.duration) ? Math.max(0, this.player.duration - 0.1) : time); } catch { /* A later native seek remains possible. */ } }
        this.restorePosition = null;
      };
      this.player.addEventListener('loadedmetadata', this.restorePosition, { once: true });
      this.player.load();
      this.resumeAt = time;
      this.switching = false;
      this.wanted = resume;
      if (this.errorNode) this.errorNode.hidden = true;
      if (this.retryButton) this.retryButton.hidden = true;
      this.status('');
      if (resume) this.requestPlay();
    }
    changeQuality(mode) {
      const time = this.player.currentTime || 0;
      const resume = this.wanted || !this.player.paused;
      this.resetPlayback();
      this.mode = mode;
      this.fallbackTried = false;
      if (this.qualitySelect) this.qualitySelect.value = mode;
      this.installSource(this.defaultSource(), time, resume);
      this.metric('quality');
      // An in-progress MP4 continues directly; adaptive setup happens before the next start.
      if (!resume) this.prepareAdaptive();
    }
    defaultSource() {
      return this.mode === 'auto' && !this.weakConnection && this.autoSources().find(item => isHls(item) && this.player.canPlayType(item.type)) || this.mp4Source();
    }
    async prepareAdaptive() {
      if (this.mode !== 'auto' || this.weakConnection || this.fallbackTried || this.hls || this.sourceKind === 'native_hls' || this.wanted) return;
      const manifest = this.autoSources().find(isHls);
      if (!manifest) return;
      if (this.preparing) return this.preparing;
      const generation = this.generation;
      this.preparing = (async () => {
        const Hls = await loadHls(this.hlsUrl);
        if (!Hls?.isSupported() || !this.isConnected || generation !== this.generation || this.wanted) return;
        try {
          const hls = new Hls({ autoStartLoad: false, startPosition: this.resumeAt || 0, startLevel: 0, capLevelToPlayerSize: true, maxBufferLength: 15, maxMaxBufferLength: 30, backBufferLength: 10, lowLatencyMode: false });
          this.hls = hls;
          this.sourceKind = 'hls';
          hls.on(Hls.Events.MANIFEST_PARSED, () => { if (generation === this.generation && this.wanted) hls.startLoad(this.player.currentTime || this.resumeAt || -1); });
          hls.on(Hls.Events.ERROR, (_event, info) => { if (info.fatal && generation === this.generation) this.recoverPlayback('hls_error'); });
          this.switching = true;
          this.player.removeAttribute('src');
          this.player.replaceChildren();
          hls.attachMedia(this.player);
          hls.loadSource(manifest.src.startsWith('//') ? 'https:' + manifest.src : manifest.src);
          this.switching = false;
        } catch { this.switching = false; if (generation === this.generation) this.recoverPlayback('hls_setup'); }
      })();
      return this.preparing;
    }
    requestPlay() {
      this.wanted = true;
      this.startRequested = this.startRequested || performance.now();
      this.hls?.startLoad(this.player.currentTime || this.resumeAt || -1);
      const generation = this.generation;
      const attempt = this.player.play();
      if (attempt?.catch) attempt.catch(() => {
        if (generation !== this.generation) return;
        this.wanted = false;
        this.status('');
        if (this.playButton) this.playButton.hidden = false;
      });
    }
    watchStall() {
      if (this.stallTimer) return;
      this.stallTimer = setTimeout(() => {
        this.stallTimer = null;
        if (!this.wanted || this.player.seeking) return;
        if (this.mode === 'auto' && !this.fallbackTried) this.recoverPlayback('stall');
        else this.failPlayback();
      }, 8000);
    }
    recoverPlayback(reason) {
      this.metric('error', { reason });
      if (this.fallbackTried || (this.sourceKind === 'mp4' && this.mode !== 'auto')) { this.failPlayback(); return; }
      const time = this.player.currentTime || 0;
      const resume = Boolean(this.wanted);
      const next = this.mp4Source(480) || this.mp4Source();
      const currentUrl = this.player.querySelector('source')?.src;
      const nextUrl = next?.src?.startsWith('//') ? 'https:' + next.src : next?.src;
      this.resetPlayback();
      this.fallbackTried = true;
      this.installSource(next, time, resume);
      this.status(currentUrl === nextUrl ? 'loading' : 'switched');
    }
    failPlayback() {
      clearTimeout(this.stallTimer);
      this.stallTimer = null;
      if (this.errorNode) this.errorNode.hidden = false;
      if (this.retryButton) this.retryButton.hidden = false;
      if (this.playButton) this.playButton.hidden = false;
      this.status('');
    }
    languageName(language, name) {
      if (name) return name;
      if (language === 'und') return this.originalLabel;
      try { return new Intl.DisplayNames([language], { type: 'language' }).of(language) || language; } catch { return language; }
    }
    versionInfo(clip) {
      const language = normalize(clip.language);
      if (language !== 'und') return { label: this.languageName(language, clip.languageName), language };
      return declaredVersions.find(version => version.pattern.test(clip.title));
    }
    setupChooser() {
      if (!this.chooser || !this.clipSelect) return;
      this.clipSelect.replaceChildren();
      this.clips.forEach(clip => {
        const option = document.createElement('option');
        option.value = clip.id;
        option.textContent = clip.title;
        this.clipSelect.append(option);
      });
      const count = this.querySelector('[data-video-count]');
      if (count) count.textContent = (this.clips.length === 1 ? count.dataset.countOne : count.dataset.countOther).replace('[count]', String(this.clips.length));
      this.chooser.hidden = this.clips.length < 2;
      if (!this.versionGroup) return;
      this.versionGroup.replaceChildren();
      const versions = new Map();
      this.clips.forEach(clip => {
        const version = this.versionInfo(clip);
        if (version && !versions.has(version.language)) versions.set(version.language, { ...version, clip });
      });
      versions.forEach(version => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'amazear-video__version';
        button.dataset.videoVersion = version.language;
        button.dataset.clipId = version.clip.id;
        button.lang = version.language;
        button.textContent = version.label;
        button.setAttribute('aria-pressed', 'false');
        this.versionGroup.append(button);
        this.versionButtons.push(button);
      });
      this.versionGroup.hidden = versions.size < 2;
    }
    setLanguage(language, play, clipId) {
      if (!this.languages.includes(language)) return;
      const oldTopic = this.currentClip?.topic;
      const visible = this.clips.filter(clip => normalize(clip.language) === language);
      this.select.value = language;
      this.buttons.forEach(button => { button.hidden = !visible.some(clip => clip.id === button.dataset.clipId); });
      this.showClip(visible.find(clip => clip.id === clipId) || visible.find(clip => oldTopic && clip.topic === oldTopic) || visible[0], play);
    }
    showClip(clip, play) {
      if (!clip) return;
      const changed = this.currentClip?.id !== clip.id;
      this.currentClip = clip;
      if (this.clipSelect) this.clipSelect.value = clip.id;
      const version = this.versionInfo(clip);
      this.versionButtons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.videoVersion === version?.language)));
      if (changed) {
        this.resetPlayback();
        this.fallbackTried = false;
        this.started = false;
        this.startRequested = 0;
        this.waitStarted = 0;
        this.wanted = false;
        this.updateQualities();
        this.installSource(this.defaultSource());
        if (this.playButton) { this.playButton.hidden = false; this.playButton.disabled = false; }
        this.player.poster = clip.poster || '';
      }
      this.player.setAttribute('aria-label', clip.title);
      this.titleNode.textContent = clip.title;
      this.descriptionNode.textContent = clip.description || '';
      this.descriptionNode.hidden = !clip.description;
      this.buttons.forEach(button => button.setAttribute('aria-current', String(button.dataset.clipId === clip.id)));
      if (this.errorNode) this.errorNode.hidden = true;
      if (play) {
        this.requestPlay();
      }
    }
  }
  class AmazearVideoLibrary extends HTMLElement {
    connectedCallback() {
      if (this.onInput) return;
      const input = this.querySelector('[data-video-search-input]');
      const controls = this.querySelector('[data-video-search]');
      const cards = [...this.querySelectorAll('[data-video-guide-card]')];
      if (!input || !controls || !cards.length) return;
      const status = this.querySelector('[data-video-search-status]');
      const empty = this.querySelector('[data-video-search-empty]');
      this.onInput = () => {
        const query = input.value.trim().toLocaleLowerCase();
        let count = 0;
        cards.forEach(card => {
          card.hidden = !String(card.dataset.search).toLocaleLowerCase().includes(query);
          if (!card.hidden) count += 1;
        });
        if (status) status.textContent = String(count) + (count === 1 ? ' collection' : ' collections');
        if (empty) empty.hidden = count !== 0;
      };
      this.searchInput = input;
      controls.hidden = false;
      input.addEventListener('input', this.onInput);
      this.onInput();
    }
    disconnectedCallback() {
      this.searchInput?.removeEventListener('input', this.onInput);
      this.onInput = null;
    }
  }
  if (!customElements.get('amazear-video-guide')) customElements.define('amazear-video-guide', AmazearVideoGuide);
  if (!customElements.get('amazear-video-library')) customElements.define('amazear-video-library', AmazearVideoLibrary);
})();
